import {
    collection, addDoc, getDocs, getDoc, doc,
    updateDoc, deleteDoc, query, where, serverTimestamp, orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { sendEventRegistrationConfirmationEmail } from './emailService';

const EVENTS_COLLECTION = 'events';
const SUBMISSIONS_COLLECTION = 'event_submissions';

// ─── Collections it can auto-fill from ───────────────────────────────────────
const LOOKUP_COLLECTIONS = [
    { name: 'industrialVisitRegistrations', enrollmentField: 'enrollmentNo' },
    { name: 'webDesignRegistrations', enrollmentField: 'enrollmentNo' },
    { name: 'applications', enrollmentField: 'enrollmentNo' },
];

// ─── Auto-Fill: Fetch student data by enrollment number ───────────────────────
/**
 * Searches all known collections for a matching enrollment number.
 * Returns a normalized student profile object or null if not found.
 *
 * @param {string} enrollmentNo
 * @returns {Promise<{fullName, email, contactNumber, branchYear, section, enrollmentNo} | null>}
 */
export const fetchStudentByEnrollment = async (enrollmentNo) => {
    if (!enrollmentNo || enrollmentNo.trim().length < 3) return null;

    const normalized = enrollmentNo.trim().toUpperCase();

    for (const { name, enrollmentField } of LOOKUP_COLLECTIONS) {
        try {
            const q = query(
                collection(db, name),
                where(enrollmentField, '==', normalized)
            );
            const snapshot = await getDocs(q);

            if (!snapshot.empty) {
                const data = snapshot.docs[0].data();
                console.log(`✅ Found student in [${name}] for enrollment: ${normalized}`);

                // Normalize field names across collections
                return {
                    fullName: data.fullName || data.name || '',
                    email: data.email || '',
                    contactNumber: data.contactNumber || data.phone || data.mobile || '',
                    branchYear: data.branchYear || data.courseYear || data.course_year || '',
                    section: data.section || '',
                    enrollmentNo: normalized,
                };
            }
        } catch (err) {
            // One collection failing shouldn't stop others from being checked
            console.warn(`⚠️ Could not search collection [${name}]:`, err.message);
        }
    }

    console.log(`ℹ️ No student found for enrollment: ${normalized}`);
    return null;
};

// ─── Create Event ─────────────────────────────────────────────────────────────
/**
 * Admin creates a new dynamic event with its field schema.
 *
 * @param {Object} eventData - { title, date, location, description, imageUrl, capacity, fields, themeColor, flag }
 * @returns {Promise<string>} - The new event document ID
 */
export const createEvent = async (eventData) => {
    const docRef = await addDoc(collection(db, EVENTS_COLLECTION), {
        ...eventData,
        isRegistrationOpen: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    console.log('✅ Event created with ID:', docRef.id);
    return docRef.id;
};

// ─── Get All Events ───────────────────────────────────────────────────────────
/**
 * Fetches all events for the homepage Upcoming Events section.
 * @returns {Promise<Array>}
 */
export const getAllEvents = async () => {
    try {
        const q = query(collection(db, EVENTS_COLLECTION), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
        // Fallback without ordering if index not ready
        const snapshot = await getDocs(collection(db, EVENTS_COLLECTION));
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }
};

// ─── Get Event By ID ──────────────────────────────────────────────────────────
/**
 * Fetches a single event — used by the registration form page.
 * @param {string} eventId
 * @returns {Promise<Object|null>}
 */
export const getEventById = async (eventId) => {
    const docRef = doc(db, EVENTS_COLLECTION, eventId);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) return null;
    return { id: snapshot.id, ...snapshot.data() };
};

// ─── Update Event ─────────────────────────────────────────────────────────────
/**
 * Update any field on an event (e.g., toggle isRegistrationOpen, change capacity).
 * @param {string} eventId
 * @param {Object} updates
 */
export const updateEvent = async (eventId, updates) => {
    try {
        // Validate eventId
        if (!eventId || typeof eventId !== 'string') {
            throw new Error('Invalid event ID');
        }

        // Validate updates object
        if (!updates || typeof updates !== 'object') {
            throw new Error('Invalid updates object');
        }

        // Ensure boolean values are actually booleans (not strings)
        const sanitizedUpdates = {};
        for (const [key, value] of Object.entries(updates)) {
            if (key === 'isRegistrationOpen') {
                sanitizedUpdates[key] = Boolean(value);
            } else if (key === 'capacity') {
                sanitizedUpdates[key] = Number(value);
            } else {
                sanitizedUpdates[key] = value;
            }
        }

        const docRef = doc(db, EVENTS_COLLECTION, eventId);
        await updateDoc(docRef, {
            ...sanitizedUpdates,
            updatedAt: serverTimestamp()
        });
        console.log('✅ Event updated:', eventId);
    } catch (error) {
        console.error('❌ Failed to update event:', error);
        throw error;
    }
};

// ─── Delete Event ─────────────────────────────────────────────────────────────
/**
 * Admin deletes an event and all its submissions.
 * @param {string} eventId
 */
export const deleteEvent = async (eventId) => {
    // Delete all submissions first
    try {
        const q = query(collection(db, SUBMISSIONS_COLLECTION), where('eventId', '==', eventId));
        const snapshot = await getDocs(q);
        const deletions = snapshot.docs.map(d => deleteDoc(d.ref));
        await Promise.all(deletions);
        console.log(`Deleted ${snapshot.size} submissions for event ${eventId}`);
    } catch (err) {
        console.warn('Could not delete submissions:', err.message);
    }

    // Delete the event itself
    await deleteDoc(doc(db, EVENTS_COLLECTION, eventId));
    console.log('✅ Event deleted:', eventId);
};

// ─── Submit Event Form ────────────────────────────────────────────────────────
/**
 * Student submits a registration form. Checks capacity and duplicates first.
 *
 * @param {string} eventId
 * @param {Object} responses - Key-value pairs matching event.fields labels
 * @param {Object} eventMeta - { title, date, location, capacity, isRegistrationOpen }
 * @returns {Promise<string>} - Submission document ID
 */
export const submitEventForm = async (eventId, responses, eventMeta) => {
    // 1. Check registration is open
    if (!eventMeta.isRegistrationOpen) {
        throw new Error('Registration for this event is currently closed.');
    }

    // 2. Check capacity
    const existingSubmissions = await getSubmissionsForEvent(eventId);
    if (eventMeta.capacity && existingSubmissions.length >= eventMeta.capacity) {
        throw new Error('This event has reached its maximum capacity.');
    }

    // 3. Check for duplicate enrollment (if enrollment field exists)
    const enrollmentValue = responses['Enrollment Number'] || responses['Enrollment No'] || responses['enrollmentNo'];
    if (enrollmentValue) {
        const normalizedEnrollment = enrollmentValue.toString().toUpperCase().trim();
        const duplicate = existingSubmissions.find(sub => {
            const existingEnrollment = (
                sub.responses?.['Enrollment Number'] ||
                sub.responses?.['Enrollment No'] ||
                sub.responses?.['enrollmentNo'] || ''
            ).toString().toUpperCase().trim();
            return existingEnrollment === normalizedEnrollment;
        });
        if (duplicate) {
            throw new Error('This enrollment number is already registered for this event.');
        }
    }

    // 4. Check for duplicate email
    const emailValue = responses['Email'] || responses['email'] || responses['Email Address'];
    if (emailValue) {
        const normalizedEmail = emailValue.toString().toLowerCase().trim();
        const duplicate = existingSubmissions.find(sub => {
            const existingEmail = (
                sub.responses?.['Email'] ||
                sub.responses?.['email'] ||
                sub.responses?.['Email Address'] || ''
            ).toString().toLowerCase().trim();
            return existingEmail === normalizedEmail;
        });
        if (duplicate) {
            throw new Error('This email address is already registered for this event.');
        }
    }

    // 5. Save the submission
    const docRef = await addDoc(collection(db, SUBMISSIONS_COLLECTION), {
        eventId,
        eventTitle: eventMeta.title,
        responses,
        submittedAt: serverTimestamp(),
    });

    console.log('✅ Form submitted for event:', eventMeta.title, '| Submission ID:', docRef.id);

    // 6. Send confirmation email if email is in responses
    if (emailValue) {
        try {
            const name = responses['Full Name'] || responses['Name'] || responses['fullName'] || 'Student';
            console.log('📧 Attempting to send email to:', emailValue);
            const emailResult = await sendEventRegistrationConfirmationEmail({
                eventTitle: eventMeta.title,
                eventDate: eventMeta.date || '',
                eventLocation: eventMeta.location || '',
                applicantName: name,
                applicantEmail: emailValue,
                formResponses: responses,
            });
            console.log('✅ Email service response:', emailResult);
        } catch (emailErr) {
            // Email failure should not fail the submission
            console.error('❌ Confirmation email failed:', emailErr.message);
        }
    }

    return docRef.id;
};

// ─── Get Submissions for Event ────────────────────────────────────────────────
/**
 * Admin fetches all submissions for a given event.
 * @param {string} eventId
 * @returns {Promise<Array>}
 */
export const getSubmissionsForEvent = async (eventId) => {
    try {
        const q = query(
            collection(db, SUBMISSIONS_COLLECTION),
            where('eventId', '==', eventId),
            orderBy('submittedAt', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
        // Fallback without ordering
        const q = query(collection(db, SUBMISSIONS_COLLECTION), where('eventId', '==', eventId));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }
};

// ─── Get All Event Submissions ────────────────────────────────────────────────
/**
 * Admin fetches all submissions for all dynamic events.
 * @returns {Promise<Array>}
 */
export const getAllEventSubmissions = async () => {
    try {
        const q = query(collection(db, SUBMISSIONS_COLLECTION), orderBy('submittedAt', 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
        // Fallback without ordering
        const snapshot = await getDocs(collection(db, SUBMISSIONS_COLLECTION));
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }
};

// ─── Update Attended Status ────────────────────────────────────────────────
/**
 * Update attended status for a dynamic event submission
 * @param {string} submissionId
 * @param {boolean} attended
 */
export const updateEventSubmissionAttendedStatus = async (submissionId, attended) => {
    const docRef = doc(db, SUBMISSIONS_COLLECTION, submissionId);
    await updateDoc(docRef, { attended });
    console.log(`✅ Attended status updated for submission ${submissionId}: ${attended}`);
};

// ─── Delete Submission ────────────────────────────────────────────────────────
/**
 * Admin deletes an individual submission.
 * @param {string} submissionId
 */
export const deleteEventSubmission = async (submissionId) => {
    await deleteDoc(doc(db, SUBMISSIONS_COLLECTION, submissionId));
    console.log('✅ Submission deleted:', submissionId);
};

// ─── Get Submission Count for Event ──────────────────────────────────────────
/**
 * Quick count of how many registrations an event has (for capacity progress bar).
 * @param {string} eventId
 * @returns {Promise<number>}
 */
export const getSubmissionCountForEvent = async (eventId) => {
    const submissions = await getSubmissionsForEvent(eventId);
    return submissions.length;
};
